import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Plus,
  Search,
  Edit2,
  Trash2,
  Upload,
  FileText,
  LayoutGrid,
  LayoutList,
  Filter,
  SortAsc,
  FlaskConical,
  Image as ImageIcon,
  FileIcon,
  CheckSquare,
  GripVertical,
  X
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './AdminEstacoesNovo.css';

const AdminEstacoesNovo = () => {
  const navigate = useNavigate();
  const API = '/api';

  // States
  const [estacoes, setEstacoes] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEspecialidade, setFilterEspecialidade] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('nome'); // nome, criadoEm
  const [viewMode, setViewMode] = useState('table'); // table or grouped
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoteiroModal, setShowRoteiroModal] = useState(false);
  const [showExamesModal, setShowExamesModal] = useState(false);
  const [selectedEstacao, setSelectedEstacao] = useState(null);
  const [uploadingMedico, setUploadingMedico] = useState(false);
  const [uploadingAtor, setUploadingAtor] = useState(false);
  const [exames, setExames] = useState([]);
  const [examesLoading, setExamesLoading] = useState(false);
  const [uploadingExame, setUploadingExame] = useState(false);
  const [newExameNome, setNewExameNome] = useState('');
  const [newExameTipo, setNewExameTipo] = useState('image');

  // Checklist states
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [showChecklistItemForm, setShowChecklistItemForm] = useState(false);
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [checklistItemForm, setChecklistItemForm] = useState({
    order: 0,
    title: '',
    description: '',
    bullets: [],
    scoreAdequate: 1.0,
    scorePartial: 0.5,
    scoreInadequate: 0.0
  });
  const [newBullet, setNewBullet] = useState('');

  // Form states
  const [newEstacao, setNewEstacao] = useState({
    nome: '',
    descricaoCurta: '',
    especialidadeId: '',
    tempoMaximoMinutos: 30,
    duracao_minutos: null, // NEW: Duration for shared timer
    ordem: 0,
    ativo: true,
    checklistMedico: '',
    checklistAtor: '',
    checklistAvaliador: ''
  });

  const [editEstacao, setEditEstacao] = useState({
    nome: '',
    descricaoCurta: '',
    especialidadeId: '',
    tempoMaximoMinutos: 30,
    duracao_minutos: null, // NEW: Duration for shared timer
    ativo: true,
    ordem: 0,
    checklistMedico: '',
    checklistAtor: '',
    checklistAvaliador: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Reset filters function
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterEspecialidade('');
    setFilterStatus('');
    console.log('[ADM ESTAÇÕES] Filters reset to default');
  };

  const fetchData = async () => {
    try {
      console.log('[ADM ESTAÇÕES] Fetching from:', `${API}/estacoes`, `${API}/especialidades`);
      
      const [estacoesRes, especialidadesRes] = await Promise.all([
        axios.get(`${API}/estacoes`),
        axios.get(`${API}/especialidades`)
      ]);

      console.log('[ADM ESTAÇÕES] Success! Estações:', estacoesRes.data.length, 'Especialidades:', especialidadesRes.data.length);
      setEstacoes(estacoesRes.data);
      setEspecialidades(especialidadesRes.data);
    } catch (error) {
      console.error('[ADM ESTAÇÕES] Error fetching:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        endpoints: [`${API}/estacoes`, `${API}/especialidades`]
      });
      
      // Only show toast if not 401 (interceptor handles it)
      if (!error.response || error.response.status !== 401) {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted estacoes
  const filteredEstacoes = estacoes
    .filter(e => {
      const matchSearch = e.nome.toLowerCase().includes(searchTerm.toLowerCase());
      const matchEspecialidade = !filterEspecialidade || e.especialidadeId === filterEspecialidade;
      const matchStatus = filterStatus === '' || (filterStatus === 'ativa' ? e.ativo : !e.ativo);
      return matchSearch && matchEspecialidade && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'nome') return a.nome.localeCompare(b.nome);
      if (sortBy === 'criadoEm') return new Date(b.criadoEm) - new Date(a.criadoEm);
      return a.ordem - b.ordem; // default: ordem
    });

  // Group by especialidade for grouped view
  const groupedEstacoes = especialidades.map(esp => ({
    especialidade: esp,
    estacoes: filteredEstacoes.filter(e => e.especialidadeId === esp.id)
  })).filter(group => group.estacoes.length > 0);

  const handleCreate = async () => {
    if (!newEstacao.nome || !newEstacao.especialidadeId) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      console.log('[ADM ESTAÇÕES][CREATE] Creating estacao:', newEstacao.nome);
      const response = await axios.post(`${API}/admin/estacoes`, newEstacao);
      console.log('[ADM ESTAÇÕES][CREATE] Success! ID:', response.data.id);
      
      toast.success('Estação criada com sucesso');
      setShowCreateModal(false);
      setNewEstacao({
        nome: '',
        descricaoCurta: '',
        especialidadeId: '',
        tempoMaximoMinutos: 30,
        duracao_minutos: null, // NEW: Reset duration field
        ordem: 0,
        ativo: true,
        checklistMedico: '',
        checklistAtor: '',
        checklistAvaliador: ''
      });
      
      // Add to list immediately without refetch
      setEstacoes(prev => [response.data, ...prev]);
    } catch (error) {
      console.error('[ADM ESTAÇÕES][CREATE] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      toast.error(error.response?.data?.detail || 'Erro ao criar estação');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedEstacao) return;
    
    setLoading(true);
    try {
      console.log('[ADM ESTAÇÕES][EDIT] Updating estacao:', selectedEstacao.id, editEstacao.nome);
      const response = await axios.put(`${API}/admin/estacoes/${selectedEstacao.id}`, editEstacao);
      console.log('[ADM ESTAÇÕES][EDIT] Success!');
      
      toast.success('Estação atualizada com sucesso');
      setShowEditModal(false);
      setSelectedEstacao(null);
      
      // Update in list immediately without refetch
      setEstacoes(prev => prev.map(e => 
        e.id === selectedEstacao.id ? response.data : e
      ));
    } catch (error) {
      console.error('[ADM ESTAÇÕES][EDIT] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        estacaoId: selectedEstacao.id
      });
      toast.error(error.response?.data?.detail || 'Erro ao atualizar estação');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (estacao) => {
    if (!window.confirm(`Tem certeza que deseja excluir a estação "${estacao.nome}"?`)) return;

    try {
      console.log('[ADM ESTAÇÕES][DELETE] Deleting estacao:', estacao.id, estacao.nome);
      await axios.delete(`${API}/admin/estacoes/${estacao.id}`);
      console.log('[ADM ESTAÇÕES][DELETE] Success!');
      
      toast.success('Estação excluída com sucesso');
      
      // Remove from list immediately without refetch
      setEstacoes(prev => prev.filter(e => e.id !== estacao.id));
    } catch (error) {
      console.error('[ADM ESTAÇÕES][DELETE] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        estacaoId: estacao.id
      });
      toast.error(error.response?.data?.detail || 'Erro ao excluir estação');
    }
  };

  const handleToggleStatus = async (estacao) => {
    try {
      await axios.put(`${API}/api/admin/estacoes/${estacao.id}`, {
        ativo: !estacao.ativo
      });
      toast.success(`Estação ${!estacao.ativo ? 'ativada' : 'desativada'}`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const openEditModal = (estacao) => {
    setSelectedEstacao(estacao);
    setEditEstacao({
      nome: estacao.nome,
      descricaoCurta: estacao.descricaoCurta,
      especialidadeId: estacao.especialidadeId,
      tempoMaximoMinutos: estacao.tempoMaximoMinutos,
      duracao_minutos: estacao.duracao_minutos || null, // NEW: Load duration if exists
      ativo: estacao.ativo,
      ordem: estacao.ordem,
      checklistMedico: estacao.checklistMedico || '',
      checklistAtor: estacao.checklistAtor || '',
      checklistAvaliador: estacao.checklistAvaliador || ''
    });
    setShowEditModal(true);
  };

  const openRoteiroModal = (estacao) => {
    setSelectedEstacao(estacao);
    setShowRoteiroModal(true);
  };

  const handleUploadRoteiro = async (papel, file) => {
    if (!file || !selectedEstacao) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Apenas arquivos PDF são permitidos');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 50MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    papel === 'medico' ? setUploadingMedico(true) : setUploadingAtor(true);

    try {
      console.log(`[UPLOAD ROTEIRO] Uploading ${papel} PDF for estacao:`, selectedEstacao.id);
      
      const response = await axios.post(
        `${API}/admin/estacoes/${selectedEstacao.id}/roteiro/${papel}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      console.log(`[UPLOAD ROTEIRO] Success! Firebase path:`, response.data.firebase_url);
      toast.success(`Roteiro de ${papel} enviado com sucesso`);
      
      // Update local state immediately without closing modal
      const updatedEstacao = { ...selectedEstacao };
      if (papel === 'medico') {
        updatedEstacao.roteiroMedicoUrl = response.data.firebase_url;
        updatedEstacao.roteiroMedicoDisponivel = true;
      } else {
        updatedEstacao.roteiroAtorUrl = response.data.firebase_url;
        updatedEstacao.roteiroAtorDisponivel = true;
      }
      setSelectedEstacao(updatedEstacao);
      
      // Also update in main list
      setEstacoes(prev => prev.map(e => 
        e.id === selectedEstacao.id ? updatedEstacao : e
      ));
    } catch (error) {
      console.error(`[UPLOAD ROTEIRO] Error:`, {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        papel,
        estacaoId: selectedEstacao.id
      });
      toast.error(error.response?.data?.detail || `Erro ao enviar roteiro de ${papel}`);
    } finally {
      papel === 'medico' ? setUploadingMedico(false) : setUploadingAtor(false);
    }
  };

  const handleDeleteRoteiro = async (papel) => {
    if (!window.confirm(`Tem certeza que deseja excluir o roteiro do ${papel}?`)) return;

    try {
      console.log(`[UPLOAD ROTEIRO][DELETE] Deleting ${papel} PDF for estacao:`, selectedEstacao.id);
      
      await axios.delete(`${API}/admin/estacoes/${selectedEstacao.id}/roteiro/${papel}`);
      console.log(`[UPLOAD ROTEIRO][DELETE] Success!`);
      
      toast.success(`Roteiro de ${papel} excluído com sucesso`);
      
      // Update local state immediately without closing modal
      const updatedEstacao = { ...selectedEstacao };
      if (papel === 'medico') {
        updatedEstacao.roteiroMedicoUrl = null;
        updatedEstacao.roteiroMedicoDisponivel = false;
      } else {
        updatedEstacao.roteiroAtorUrl = null;
        updatedEstacao.roteiroAtorDisponivel = false;
      }
      setSelectedEstacao(updatedEstacao);
      
      // Also refresh the list in background
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao excluir roteiro de ${papel}`);
    }
  };

  // Exames functions
  const handleOpenExamesModal = async (estacao) => {
    setSelectedEstacao(estacao);
    setShowExamesModal(true);
    fetchExames(estacao.id);
  };

  const fetchExames = async (estacaoId) => {
    setExamesLoading(true);
    try {
      console.log('[ADM EXAMES] Fetching exames for estacao:', estacaoId);
      const response = await axios.get(`${API}/admin/estacoes/${estacaoId}/exames`);
      console.log('[ADM EXAMES] Success! Count:', response.data.length);
      setExames(response.data);
    } catch (error) {
      console.error('[ADM EXAMES] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        estacaoId
      });
      // Only show toast for real errors, not 404 (empty list is valid)
      if (error.response?.status !== 401 && error.response?.status !== 404) {
        toast.error('Erro ao carregar exames');
      }
      // Empty list on error (will show "Nenhum exame cadastrado")
      setExames([]);
    } finally {
      setExamesLoading(false);
    }
  };

  const handleUploadExame = async (file) => {
    if (!file || !selectedEstacao || !newExameNome.trim()) {
      toast.error('Preencha o nome do exame e selecione um arquivo');
      return;
    }

    // Validate file type
    const extension = file.name.split('.').pop().toLowerCase();
    if (newExameTipo === 'pdf' && extension !== 'pdf') {
      toast.error('Para tipo PDF, selecione um arquivo .pdf');
      return;
    }
    if (newExameTipo === 'image' && !['png', 'jpg', 'jpeg'].includes(extension)) {
      toast.error('Para tipo Imagem, selecione PNG, JPG ou JPEG');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 50MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('nome', newExameNome.trim());
    formData.append('tipo', newExameTipo);

    setUploadingExame(true);
    try {
      console.log('[UPLOAD EXAME] Uploading:', {
        nome: newExameNome.trim(),
        tipo: newExameTipo,
        estacaoId: selectedEstacao.id,
        fileSize: file.size
      });
      
      const response = await axios.post(
        `${API}/admin/estacoes/${selectedEstacao.id}/exames`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      console.log('[UPLOAD EXAME] Success! Exame ID:', response.data.id);
      toast.success('Exame adicionado com sucesso');
      setNewExameNome('');
      
      // Add to list immediately
      setExames(prev => [...prev, response.data]);
    } catch (error) {
      console.error('[UPLOAD EXAME] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        estacaoId: selectedEstacao.id
      });
      toast.error(error.response?.data?.detail || 'Erro ao adicionar exame');
    } finally {
      setUploadingExame(false);
    }
  };

  const handleDeleteExame = async (exameId) => {
    if (!window.confirm('Tem certeza que deseja excluir este exame?')) return;

    try {
      console.log('[UPLOAD EXAME][DELETE] Deleting exame:', exameId);
      
      await axios.delete(`${API}/admin/exames/${exameId}`);
      console.log('[UPLOAD EXAME][DELETE] Success!');
      
      toast.success('Exame excluído com sucesso');
      
      // Remove from list immediately
      setExames(prev => prev.filter(e => e.id !== exameId));
    } catch (error) {
      console.error('[UPLOAD EXAME][DELETE] Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        exameId
      });
      toast.error(error.response?.data?.detail || 'Erro ao excluir exame');
    }
  };

  // ========== CHECKLIST FUNCTIONS ==========
  const handleOpenChecklistModal = async (estacao) => {
    setSelectedEstacao(estacao);
    setShowChecklistModal(true);
    fetchChecklistItems(estacao.id);
  };

  const fetchChecklistItems = async (estacaoId) => {
    setChecklistLoading(true);
    try {
      console.log('[ADM CHECKLIST] Fetching items for estacao:', estacaoId);
      const response = await axios.get(`${API}/checklist/items/${estacaoId}`);
      console.log('[ADM CHECKLIST] Success! Items:', response.data.length);
      setChecklistItems(response.data);
    } catch (error) {
      console.error('[ADM CHECKLIST] Error fetching items:', error.response?.data?.detail || error.message);
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar checklist');
      }
      setChecklistItems([]);
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleOpenChecklistItemForm = (item = null) => {
    if (item) {
      // Edit mode
      setEditingChecklistItem(item);
      setChecklistItemForm({
        order: item.order,
        title: item.title,
        description: item.description || '',
        bullets: item.bullets || [],
        scoreAdequate: item.scoreAdequate,
        scorePartial: item.scorePartial,
        scoreInadequate: item.scoreInadequate
      });
    } else {
      // Create mode - use next order number
      const nextOrder = checklistItems.length > 0 
        ? Math.max(...checklistItems.map(i => i.order)) + 1 
        : 1;
      setEditingChecklistItem(null);
      setChecklistItemForm({
        order: nextOrder,
        title: '',
        description: '',
        bullets: [],
        scoreAdequate: 1.0,
        scorePartial: 0.5,
        scoreInadequate: 0.0
      });
    }
    setShowChecklistItemForm(true);
  };

  const handleAddBullet = () => {
    if (newBullet.trim()) {
      setChecklistItemForm(prev => ({
        ...prev,
        bullets: [...prev.bullets, newBullet.trim()]
      }));
      setNewBullet('');
    }
  };

  const handleRemoveBullet = (index) => {
    setChecklistItemForm(prev => ({
      ...prev,
      bullets: prev.bullets.filter((_, i) => i !== index)
    }));
  };

  const handleMoveBulletUp = (index) => {
    if (index === 0) return;
    setChecklistItemForm(prev => {
      const newBullets = [...prev.bullets];
      [newBullets[index - 1], newBullets[index]] = [newBullets[index], newBullets[index - 1]];
      return { ...prev, bullets: newBullets };
    });
  };

  const handleMoveBulletDown = (index) => {
    if (index === checklistItemForm.bullets.length - 1) return;
    setChecklistItemForm(prev => {
      const newBullets = [...prev.bullets];
      [newBullets[index], newBullets[index + 1]] = [newBullets[index + 1], newBullets[index]];
      return { ...prev, bullets: newBullets };
    });
  };

  const handleSaveChecklistItem = async () => {
    if (!checklistItemForm.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      if (editingChecklistItem) {
        // Update existing item
        console.log('[ADM CHECKLIST] Updating item:', editingChecklistItem.id);
        await axios.put(`${API}/checklist/items/${editingChecklistItem.id}`, checklistItemForm);
        toast.success('Item atualizado com sucesso');
      } else {
        // Create new item
        console.log('[ADM CHECKLIST] Creating new item for estacao:', selectedEstacao.id);
        await axios.post(`${API}/checklist/items`, {
          ...checklistItemForm,
          estacaoId: selectedEstacao.id
        });
        toast.success('Item criado com sucesso');
      }
      
      setShowChecklistItemForm(false);
      fetchChecklistItems(selectedEstacao.id);
    } catch (error) {
      console.error('[ADM CHECKLIST] Error saving item:', error.response?.data?.detail || error.message);
      toast.error(error.response?.data?.detail || 'Erro ao salvar item');
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (!window.confirm('Tem certeza que deseja excluir este item do checklist?')) return;

    try {
      console.log('[ADM CHECKLIST] Deleting item:', itemId);
      await axios.delete(`${API}/checklist/items/${itemId}`);
      toast.success('Item excluído com sucesso');
      fetchChecklistItems(selectedEstacao.id);
    } catch (error) {
      console.error('[ADM CHECKLIST] Error deleting item:', error.response?.data?.detail || error.message);
      toast.error(error.response?.data?.detail || 'Erro ao excluir item');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(checklistItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately
    setChecklistItems(items);

    // Send new order to backend
    try {
      const itemIds = items.map(item => item.id);
      console.log('[ADM CHECKLIST] Reordering items:', itemIds);
      await axios.put(`${API}/checklist/items/reorder`, { itemIds });
      toast.success('Ordem atualizada');
    } catch (error) {
      console.error('[ADM CHECKLIST] Error reordering:', error.response?.data?.detail || error.message);
      toast.error('Erro ao atualizar ordem');
      // Refresh to restore correct order
      fetchChecklistItems(selectedEstacao.id);
    }
  };

  // Format helpers for checklist description/bullets
  const applyFormatToTextarea = (textareaRef, formatStart, formatEnd) => {
    if (!textareaRef) return;
    
    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const text = textareaRef.value;
    const selectedText = text.substring(start, end);
    
    let newText;
    let newCursorPos;
    
    if (selectedText) {
      // Wrap selected text
      newText = text.substring(0, start) + formatStart + selectedText + formatEnd + text.substring(end);
      newCursorPos = end + formatStart.length + formatEnd.length;
    } else {
      // Insert format markers with cursor in the middle
      newText = text.substring(0, start) + formatStart + formatEnd + text.substring(end);
      newCursorPos = start + formatStart.length;
    }
    
    return { newText, newCursorPos };
  };

  const handleBoldDescription = () => {
    const textarea = document.querySelector('textarea[placeholder*="Descrição"]');
    const result = applyFormatToTextarea(textarea, '**', '**');
    if (result) {
      setChecklistItemForm({ ...checklistItemForm, description: result.newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(result.newCursorPos, result.newCursorPos);
      }, 0);
    }
  };

  const handleItalicDescription = () => {
    const textarea = document.querySelector('textarea[placeholder*="Descrição"]');
    const result = applyFormatToTextarea(textarea, '_', '_');
    if (result) {
      setChecklistItemForm({ ...checklistItemForm, description: result.newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(result.newCursorPos, result.newCursorPos);
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#191919] text-white flex items-center justify-center">
        <div className="text-xl">Carregando estações...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-gray-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Gerenciamento de Estações</h1>
                <p className="text-sm text-gray-400">
                  {loading 
                    ? 'Carregando...' 
                    : `${filteredEstacoes.length} ${filteredEstacoes.length === 1 ? 'estação encontrada' : 'estações encontradas'}`
                  }
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#CF0D1D] hover:bg-[#A00A17] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Estação
            </Button>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="container mx-auto px-4 py-6">
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
              </div>

              {/* Filter by Matéria */}
              <select
                value={filterEspecialidade}
                onChange={(e) => setFilterEspecialidade(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="">Todas as Matérias</option>
                {especialidades.map(esp => (
                  <option key={esp.id} value={esp.id}>{esp.nome}</option>
                ))}
              </select>

              {/* Filter by Status */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="">Todos os Status</option>
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="nome">Ordenar por: Nome</option>
                <option value="criadoEm">Ordenar por: Data de Criação</option>
              </select>

              {/* Clear Filters Button */}
              {(searchTerm || filterEspecialidade || filterStatus) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Limpar filtros
                </Button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-[#CF0D1D]' : 'border-gray-600 text-gray-300'}
              >
                <LayoutList className="w-4 h-4 mr-2" />
                Vista de Tabela
              </Button>
              <Button
                variant={viewMode === 'grouped' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grouped')}
                className={viewMode === 'grouped' ? 'bg-[#CF0D1D]' : 'border-gray-600 text-gray-300'}
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Agrupar por Matéria
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table View */}
        {viewMode === 'table' && (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900 border-b border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Matéria</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Roteiros</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Data Criação</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                          Carregando estações...
                        </td>
                      </tr>
                    ) : filteredEstacoes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="text-gray-400">
                            <p className="font-medium mb-2">Nenhuma estação encontrada</p>
                            {(searchTerm || filterEspecialidade || filterStatus) ? (
                              <p className="text-sm">Tente ajustar os filtros ou limpar a busca</p>
                            ) : estacoes.length === 0 ? (
                              <p className="text-sm">Clique em "Nova Estação" para começar</p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredEstacoes.map((estacao) => (
                        <tr key={estacao.id} className="hover:bg-gray-750 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-white">{estacao.nome}</div>
                              <div className="text-sm text-gray-400">{estacao.descricaoCurta}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
                              {estacao.especialidadeNome}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleToggleStatus(estacao)}
                              className={`px-2 py-1 text-xs rounded-full ${
                                estacao.ativo
                                  ? 'bg-green-900 text-green-200'
                                  : 'bg-red-900 text-red-200'
                              }`}
                            >
                              {estacao.ativo ? 'Ativa' : 'Inativa'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-1">
                              <span className={`text-xs ${estacao.roteiroMedicoDisponivel ? 'text-green-400' : 'text-gray-500'}`}>
                                👨‍⚕️
                              </span>
                              <span className={`text-xs ${estacao.roteiroAtorDisponivel ? 'text-green-400' : 'text-gray-500'}`}>
                                🎭
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">
                            {new Date(estacao.criadoEm).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openRoteiroModal(estacao)}
                                className="text-blue-400 hover:bg-gray-700"
                                title="Gerenciar Roteiros"
                              >
                                <Upload className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenExamesModal(estacao)}
                                className="text-green-400 hover:bg-gray-700"
                                title="Gerenciar Exames"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenChecklistModal(estacao)}
                                className="text-purple-400 hover:bg-gray-700"
                                title="Gerenciar Checklist"
                              >
                                <CheckSquare className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(estacao)}
                                className="text-yellow-400 hover:bg-gray-700"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(estacao)}
                                className="text-red-400 hover:bg-gray-700"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grouped View */}
        {viewMode === 'grouped' && (
          <div className="space-y-6">
            {loading ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-8 text-center text-gray-400">
                  Carregando estações...
                </CardContent>
              </Card>
            ) : groupedEstacoes.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-8 text-center">
                  <div className="text-gray-400">
                    <p className="font-medium mb-2">Nenhuma estação encontrada</p>
                    {(searchTerm || filterEspecialidade || filterStatus) ? (
                      <p className="text-sm">Tente ajustar os filtros ou limpar a busca</p>
                    ) : estacoes.length === 0 ? (
                      <p className="text-sm">Clique em "Nova Estação" para começar</p>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ) : (
              groupedEstacoes.map((group) => (
                <Card key={group.especialidade.id} className="bg-gray-800 border-gray-700">
                  <CardHeader className="bg-gray-900 border-b border-gray-700">
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <span className="text-2xl">📚</span>
                      {group.especialidade.nome}
                      <span className="ml-2 text-sm font-normal text-gray-400">
                        ({group.estacoes.length} estações)
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      {group.estacoes.map((estacao) => (
                        <div
                          key={estacao.id}
                          className="flex items-center justify-between p-4 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div>
                                <div className="font-medium text-white">{estacao.nome}</div>
                                <div className="text-sm text-gray-400">{estacao.descricaoCurta}</div>
                              </div>
                              <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                                estacao.ativo ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'
                              }`}>
                                {estacao.ativo ? 'Ativa' : 'Inativa'}
                              </span>
                              <div className="flex gap-1 ml-2">
                                <span className={`text-xs ${estacao.roteiroMedicoDisponivel ? 'text-green-400' : 'text-gray-600'}`} title="Roteiro Médico">
                                  👨‍⚕️
                                </span>
                                <span className={`text-xs ${estacao.roteiroAtorDisponivel ? 'text-green-400' : 'text-gray-600'}`} title="Roteiro Ator">
                                  🎭
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openRoteiroModal(estacao)}
                              className="text-blue-400 hover:bg-gray-600"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditModal(estacao)}
                              className="text-yellow-400 hover:bg-gray-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(estacao)}
                              className="text-red-400 hover:bg-gray-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Nova Estação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Nome da Estação *</Label>
              <Input
                value={newEstacao.nome}
                onChange={(e) => setNewEstacao({ ...newEstacao, nome: e.target.value })}
                placeholder="Ex: Avaliação de Paciente com Dor Torácica"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Descrição Curta</Label>
              <Input
                value={newEstacao.descricaoCurta}
                onChange={(e) => setNewEstacao({ ...newEstacao, descricaoCurta: e.target.value })}
                placeholder="Breve descrição da estação"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Matéria/Especialidade *</Label>
              <select
                value={newEstacao.especialidadeId}
                onChange={(e) => setNewEstacao({ ...newEstacao, especialidadeId: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="">Selecione uma matéria</option>
                {especialidades.map(esp => (
                  <option key={esp.id} value={esp.id}>{esp.nome}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Tempo Máximo (minutos)</Label>
                <Input
                  type="number"
                  value={newEstacao.tempoMaximoMinutos}
                  onChange={(e) => setNewEstacao({ ...newEstacao, tempoMaximoMinutos: parseInt(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Duração da estação (minutos)</Label>
                <Input
                  type="number"
                  placeholder="Opcional - para timer compartilhado"
                  value={newEstacao.duracao_minutos || ''}
                  onChange={(e) => setNewEstacao({ ...newEstacao, duracao_minutos: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Status</Label>
              <select
                value={newEstacao.ativo ? 'ativa' : 'inativa'}
                onChange={(e) => setNewEstacao({ ...newEstacao, ativo: e.target.value === 'ativa' })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                disabled={loading}
                className="bg-[#CF0D1D] hover:bg-[#A00A17]"
              >
                {loading ? 'Criando...' : 'Criar Estação'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Editar Estação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Nome da Estação</Label>
              <Input
                value={editEstacao.nome}
                onChange={(e) => setEditEstacao({ ...editEstacao, nome: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Descrição Curta</Label>
              <Input
                value={editEstacao.descricaoCurta}
                onChange={(e) => setEditEstacao({ ...editEstacao, descricaoCurta: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Matéria/Especialidade</Label>
              <select
                value={editEstacao.especialidadeId}
                onChange={(e) => setEditEstacao({ ...editEstacao, especialidadeId: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                {especialidades.map(esp => (
                  <option key={esp.id} value={esp.id}>{esp.nome}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Tempo Máximo (minutos)</Label>
                <Input
                  type="number"
                  value={editEstacao.tempoMaximoMinutos}
                  onChange={(e) => setEditEstacao({ ...editEstacao, tempoMaximoMinutos: parseInt(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Duração da estação (minutos)</Label>
                <Input
                  type="number"
                  placeholder="Opcional - para timer compartilhado"
                  value={editEstacao.duracao_minutos || ''}
                  onChange={(e) => setEditEstacao({ ...editEstacao, duracao_minutos: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Status</Label>
              <select
                value={editEstacao.ativo ? 'ativa' : 'inativa'}
                onChange={(e) => setEditEstacao({ ...editEstacao, ativo: e.target.value === 'ativa' })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
              >
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEdit}
                disabled={loading}
                className="bg-[#CF0D1D] hover:bg-[#A00A17]"
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Roteiro Modal */}
      {selectedEstacao && (
        <Dialog open={showRoteiroModal} onOpenChange={setShowRoteiroModal}>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-xl text-white">
                Gerenciar Roteiros - {selectedEstacao.nome}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Roteiro Médico */}
              <div className="border border-gray-700 rounded-lg p-4 bg-gray-750">
                <h3 className="text-lg font-semibold mb-3 text-blue-300">👨‍⚕️ Roteiro do Médico</h3>
                
                {selectedEstacao.roteiroMedicoDisponivel ? (
                  <div className="roteiro-status-card disponivel">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-400" />
                      <span className="status-text">✓ PDF cadastrado (Firebase Storage)</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDeleteRoteiro('medico')}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="roteiro-status-card indisponivel">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="status-text">Nenhum PDF cadastrado</span>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-300">
                    {selectedEstacao.roteiroMedicoDisponivel ? 'Substituir PDF' : 'Fazer Upload do PDF'}
                  </Label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleUploadRoteiro('medico', e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingMedico}
                    className="block w-full text-sm text-gray-300 mt-2 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {uploadingMedico && (
                    <div className="upload-loading">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                      <span>Enviando PDF do médico...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Roteiro Ator */}
              <div className="border border-gray-700 rounded-lg p-4 bg-gray-750">
                <h3 className="text-lg font-semibold mb-3 text-purple-300">🎭 Roteiro do Ator</h3>
                
                {selectedEstacao.roteiroAtorDisponivel ? (
                  <div className="roteiro-status-card disponivel">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-400" />
                      <span className="status-text">✓ PDF cadastrado (Firebase Storage)</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDeleteRoteiro('ator')}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="roteiro-status-card indisponivel">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="status-text">Nenhum PDF cadastrado</span>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-300">
                    {selectedEstacao.roteiroAtorDisponivel ? 'Substituir PDF' : 'Fazer Upload do PDF'}
                  </Label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleUploadRoteiro('ator', e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingAtor}
                    className="block w-full text-sm text-gray-300 mt-2 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {uploadingAtor && (
                    <div className="upload-loading">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                      <span>Enviando PDF do ator...</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setShowRoteiroModal(false)}
                  className="bg-gray-700 hover:bg-gray-600"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Exames Modal */}
      {selectedEstacao && (
        <Dialog open={showExamesModal} onOpenChange={setShowExamesModal}>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl text-white flex items-center gap-2">
                <FlaskConical className="w-6 h-6 text-green-400" />
                Gerenciar Exames - {selectedEstacao.nome}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Add New Exame Section */}
              <div className="border border-gray-700 rounded-lg p-4 bg-gray-750">
                <h3 className="text-lg font-semibold mb-4 text-green-300">➕ Adicionar Novo Exame</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 mb-2 block">Nome do Exame</Label>
                    <Input
                      type="text"
                      value={newExameNome}
                      onChange={(e) => setNewExameNome(e.target.value)}
                      placeholder="Ex: Hemograma, Raio-X de Tórax"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-gray-300 mb-2 block">Tipo de Arquivo</Label>
                    <select
                      value={newExameTipo}
                      onChange={(e) => setNewExameTipo(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2"
                    >
                      <option value="image">Imagem (PNG, JPG, JPEG)</option>
                      <option value="pdf">PDF</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="text-gray-300 mb-2 block">Arquivo do Exame</Label>
                  <input
                    type="file"
                    accept={newExameTipo === 'pdf' ? '.pdf' : '.png,.jpg,.jpeg'}
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleUploadExame(e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingExame || !newExameNome.trim()}
                    className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {uploadingExame && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-green-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400"></div>
                      <span>Enviando exame...</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    {newExameTipo === 'pdf' ? 'Apenas arquivos PDF' : 'Apenas PNG, JPG ou JPEG'} • Máximo 50MB
                  </p>
                </div>
              </div>

              {/* Lista de Exames */}
              <div className="border border-gray-700 rounded-lg p-4 bg-gray-750">
                <h3 className="text-lg font-semibold mb-4 text-gray-200">📋 Exames Cadastrados</h3>
                
                {examesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
                  </div>
                ) : exames.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <FlaskConical className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum exame cadastrado ainda</p>
                    <p className="text-sm mt-1">Adicione o primeiro exame acima</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {exames.map((exame) => (
                      <div
                        key={exame.id}
                        className="flex items-center justify-between bg-gray-700 rounded-lg p-3 border border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          {exame.tipo === 'image' ? (
                            <ImageIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          ) : (
                            <FileIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-white font-medium">{exame.nome}</p>
                            <p className="text-xs text-gray-400">
                              {exame.tipo === 'image' ? 'Imagem' : 'PDF'} • Adicionado em {new Date(exame.criadoEm).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteExame(exame.id)}
                          className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-700">
                <Button
                  onClick={() => setShowExamesModal(false)}
                  className="bg-gray-700 hover:bg-gray-600"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Checklist Modal */}
      {selectedEstacao && (
        <Dialog open={showChecklistModal} onOpenChange={setShowChecklistModal}>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl text-white flex items-center gap-2">
                <CheckSquare className="w-6 h-6 text-purple-400" />
                Gerenciar Checklist - {selectedEstacao.nome}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Add New Item Button */}
              <div className="flex justify-between items-center">
                <p className="text-gray-400 text-sm">
                  Configure os itens do checklist de avaliação desta estação
                </p>
                <Button
                  onClick={() => handleOpenChecklistItemForm()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Item
                </Button>
              </div>

              {/* Checklist Items List */}
              {checklistLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                  <span className="ml-3 text-gray-400">Carregando checklist...</span>
                </div>
              ) : checklistItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum item no checklist</p>
                  <p className="text-sm">Clique em "Adicionar Item" para começar</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="checklist">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-3"
                      >
                        {checklistItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-gray-700 rounded-lg p-4 border ${
                                  snapshot.isDragging ? 'border-purple-400 shadow-lg' : 'border-gray-600'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {/* Drag Handle */}
                                  <div
                                    {...provided.dragHandleProps}
                                    className="cursor-move text-gray-400 hover:text-purple-400 mt-1"
                                  >
                                    <GripVertical className="w-5 h-5" />
                                  </div>

                                  {/* Item Content */}
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-purple-400 font-semibold">
                                            #{item.order}
                                          </span>
                                          <h4 className="text-white font-medium">{item.title}</h4>
                                        </div>
                                        {item.description && (
                                          <p className="text-sm text-gray-400 mb-2">
                                            {item.description.length > 100
                                              ? `${item.description.substring(0, 100)}...`
                                              : item.description}
                                          </p>
                                        )}
                                        {item.bullets && item.bullets.length > 0 && (
                                          <div className="text-xs text-gray-400 mt-2">
                                            <span className="font-semibold">Bullets: </span>
                                            {item.bullets.slice(0, 3).join(', ')}
                                            {item.bullets.length > 3 && ` (+${item.bullets.length - 3} mais)`}
                                          </div>
                                        )}
                                        <div className="flex gap-4 mt-2 text-xs">
                                          <span className="text-green-400">
                                            ✓ Adequado: {item.scoreAdequate}
                                          </span>
                                          <span className="text-yellow-400">
                                            ≈ Parcial: {item.scorePartial}
                                          </span>
                                          <span className="text-red-400">
                                            ✗ Inadequado: {item.scoreInadequate}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Action Buttons */}
                                      <div className="flex gap-2 ml-3">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleOpenChecklistItemForm(item)}
                                          className="text-blue-400 hover:bg-gray-600"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleDeleteChecklistItem(item.id)}
                                          className="text-red-400 hover:bg-gray-600"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t border-gray-700">
                <Button
                  onClick={() => setShowChecklistModal(false)}
                  className="bg-gray-700 hover:bg-gray-600"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Checklist Item Form Modal */}
      <Dialog open={showChecklistItemForm} onOpenChange={setShowChecklistItemForm}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingChecklistItem ? 'Editar Item do Checklist' : 'Novo Item do Checklist'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Order Number */}
            <div>
              <Label className="text-gray-300">Número do Item</Label>
              <Input
                type="number"
                value={checklistItemForm.order}
                onChange={(e) => setChecklistItemForm({ ...checklistItemForm, order: parseInt(e.target.value) })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="1"
              />
            </div>

            {/* Title */}
            <div>
              <Label className="text-gray-300">Título *</Label>
              <Input
                value={checklistItemForm.title}
                onChange={(e) => setChecklistItemForm({ ...checklistItemForm, title: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Ex: Investiga presença de sintomas associados"
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-300">Descrição (opcional)</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleBoldDescription}
                    className="h-7 w-7 p-0 bg-gray-700 border-gray-600 hover:bg-gray-600 font-bold"
                    title="Negrito: **texto**"
                  >
                    B
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleItalicDescription}
                    className="h-7 w-7 p-0 bg-gray-700 border-gray-600 hover:bg-gray-600 italic"
                    title="Itálico: _texto_"
                  >
                    I
                  </Button>
                </div>
              </div>
              <textarea
                value={checklistItemForm.description}
                onChange={(e) => setChecklistItemForm({ ...checklistItemForm, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white min-h-[80px]"
                placeholder="Descrição detalhada do item..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Use **texto** para negrito e _texto_ para itálico
              </p>
            </div>

            {/* Bullets */}
            <div>
              <Label className="text-gray-300">Bullets/Subtópicos</Label>
              <div className="space-y-2 mt-2">
                {checklistItemForm.bullets.map((bullet, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-700 p-2 rounded">
                    <span className="text-gray-400">•</span>
                    <span className="flex-1 text-white">{bullet}</span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMoveBulletUp(index)}
                        disabled={index === 0}
                        className="h-7 w-7 p-0 text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMoveBulletDown(index)}
                        disabled={index === checklistItemForm.bullets.length - 1}
                        className="h-7 w-7 p-0 text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveBullet(index)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={newBullet}
                    onChange={(e) => setNewBullet(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBullet()}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Digite um novo bullet e pressione Enter"
                  />
                  <Button
                    onClick={handleAddBullet}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Scores */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-green-400">Pontuação Adequado</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={checklistItemForm.scoreAdequate}
                  onChange={(e) => setChecklistItemForm({ ...checklistItemForm, scoreAdequate: parseFloat(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-yellow-400">Pontuação Parcial</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={checklistItemForm.scorePartial}
                  onChange={(e) => setChecklistItemForm({ ...checklistItemForm, scorePartial: parseFloat(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-red-400">Pontuação Inadequado</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={checklistItemForm.scoreInadequate}
                  onChange={(e) => setChecklistItemForm({ ...checklistItemForm, scoreInadequate: parseFloat(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <Button
                variant="outline"
                onClick={() => setShowChecklistItemForm(false)}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveChecklistItem}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {editingChecklistItem ? 'Atualizar Item' : 'Criar Item'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEstacoesNovo;
